import { create } from 'zustand';
import { persist } from 'zustand/middleware';

type AnimationMode = 'system' | 'enabled' | 'disabled';

interface AnimationState {
  mode: AnimationMode;
  enabled: boolean;
  setMode: (mode: AnimationMode) => void;
  toggleEnabled: () => void;
}

const getSystemPreference = (): boolean => {
  if (typeof window === 'undefined') return true;

  // Check for prefers-reduced-motion
  const prefersReducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)',
  ).matches;
  return !prefersReducedMotion;
};

const applyAnimationPreference = (enabled: boolean) => {
  if (typeof document === 'undefined') return;

  if (enabled) {
    document.documentElement.classList.remove('no-animations');
  } else {
    document.documentElement.classList.add('no-animations');
  }
};

const getEnabledFromMode = (mode: AnimationMode): boolean => {
  switch (mode) {
    case 'enabled':
      return true;
    case 'disabled':
      return false;
    case 'system':
      return getSystemPreference();
    default:
      return getSystemPreference();
  }
};

export const useAnimations = create<AnimationState>()(
  persist(
    (set, get) => {
      if (typeof window !== 'undefined') {
        const mediaQuery = window.matchMedia(
          '(prefers-reduced-motion: reduce)',
        );
        const handleChange = () => {
          const currentState = get();

          if (currentState.mode === 'system') {
            const systemPreference = getSystemPreference();
            set({ enabled: systemPreference });
            applyAnimationPreference(systemPreference);
          }
        };

        if (mediaQuery.addEventListener) {
          mediaQuery.addEventListener('change', handleChange);
        }
      }

      return {
        mode: 'system',
        enabled: getSystemPreference(),
        setMode: (mode: AnimationMode) => {
          const enabled = getEnabledFromMode(mode);
          set({ mode, enabled });
          applyAnimationPreference(enabled);
        },
        toggleEnabled: () => {
          const { enabled } = get();
          const newEnabled = !enabled;
          const newMode = newEnabled ? 'enabled' : 'disabled';
          set({ mode: newMode, enabled: newEnabled });
          applyAnimationPreference(newEnabled);
        },
      };
    },
    {
      name: 'animations-storage',
      onRehydrateStorage: () => (state) => {
        if (state) {
          const enabled = getEnabledFromMode(state.mode);
          state.enabled = enabled;
          applyAnimationPreference(enabled);
        }
      },
    },
  ),
);

// Initialize animations on module load
if (typeof window !== 'undefined') {
  const state = useAnimations.getState();
  const enabled = getEnabledFromMode(state.mode);
  applyAnimationPreference(enabled);
}
